import exceptionIcon from '@jetbrains/icons/exception.svg'
import lockIcon from '@jetbrains/icons/lock.svg'
import ErrorMessage from '@jetbrains/ring-ui/components/error-message/error-message'
import classNames from 'classnames'
import React, {useEffect} from 'react'
import type {EntryPointProps} from 'react-relay'
import {usePreloadedQuery} from 'react-relay'

import {useAppDispatch} from '../../../../hooks/react-redux'
import {useBooleanQueryParam} from '../../../../hooks/routes'
import {getPipelinesHref} from '../../../../routes'
import {subscribeOnProjectEvents} from '../../../../utils/subscriber'
import {PROJECT_PERSISTED} from '../../../../utils/subscriptionEvents'
import Loader from '../../../common/Loader/Loader'
import type {PipelinesPagesHeadQuery} from '../__generated__/PipelinesPagesHeadQuery.graphql'
import FullScreenErrorWrapper from '../components/common/FullScreenErrorWrapper/FullScreenErrorWrapper'
import Link from '../components/common/Link/Link'
import PageWithSidebar from '../components/common/PageWithSidebar/PageWithSidebar'
import {usePipelineId} from '../hooks/pipeline'
import usePermissionCanEditProject from '../hooks/usePermissionCanEditProject'
import {pipelineHeadQuery} from '../PipelinesPages.queries'
import {pipelinesApi} from '../services/pipelinesApi'
import {serializeError} from '../utils/error'

import PipelineChart from './EditPipelineChart/EditPipelineChart'
import EditPipelineHeader from './EditPipelineHeader/EditPipelineHeader'
import EditPipelineToolbar from './EditPipelineToolbar/EditPipelineToolbar'
import {EditPipelineYAML} from './EditPipelineYAML/EditPipelineYAML'

import styles from './EditPipelinePage.css'

type Queries = {pipelineHead: PipelinesPagesHeadQuery}

export default function EditPipelinePage({queries}: EntryPointProps<Queries, {}, {}, {}>) {
  const {project} = usePreloadedQuery(pipelineHeadQuery, queries.pipelineHead)
  const id = usePipelineId()
  const dispatch = useAppDispatch()
  const canEditPipeline = usePermissionCanEditProject()
  const pipeline = pipelinesApi.endpoints.getPipelineById.useQuery(id, {
    selectFromResult: ({currentData, isLoading, error}) => ({currentData, isLoading, error}),
    refetchOnMountOrArgChange: true,
  })
  const isYaml = useBooleanQueryParam('yaml')

  useEffect(
    () =>
      id
        ? subscribeOnProjectEvents(id, [PROJECT_PERSISTED], () => {
            dispatch(pipelinesApi.endpoints.getPipelineById.initiate(id))
          })
        : undefined,
    [dispatch, id],
  )

  if (!canEditPipeline) {
    return (
      <FullScreenErrorWrapper>
        <ErrorMessage
          icon={lockIcon}
          message="Access denied"
          description="You do not have enough permissions to edit this pipeline"
        >
          <Link to={getPipelinesHref()}>{'Go to the home page'}</Link>
        </ErrorMessage>
      </FullScreenErrorWrapper>
    )
  }

  if (pipeline.isLoading) {
    return <Loader />
  }

  if (pipeline.error) {
    return (
      <FullScreenErrorWrapper>
        <ErrorMessage
          icon={exceptionIcon}
          message="Error while loading pipeline"
          description={serializeError(pipeline.error)}
        >
          <Link to={getPipelinesHref()}>{'Go to the home page'}</Link>
        </ErrorMessage>
      </FullScreenErrorWrapper>
    )
  }

  return (
    <PageWithSidebar
      className={classNames({[styles.openSidebar]: !isYaml})}
      isOpenSidebar={!isYaml}
      pipelineHeadKey={project}
    >
      <EditPipelineHeader pipelineHeadKey={project} className={styles.header} />
      <div className={styles.main}>
        <EditPipelineToolbar className={styles.toolbar} />
        <div className={styles.content}>
          {isYaml ? (
            <EditPipelineYAML />
          ) : (
            <div className={styles.chart}>
              <PipelineChart pipelineHeadKey={project} />
            </div>
          )}
        </div>
      </div>
    </PageWithSidebar>
  )
}

export type EditPipelinePageComponent = typeof EditPipelinePage
